/**
 * 模組頁面代碼 : 1430-UI-02
 * 頁面說明 : 部門列表畫面
 * @module @/modules/dispatch-calendar/components/CalendarModal
 */

// ----- import from npm modules ----
import $ from 'jquery';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Menu, MenuItem, Typeahead} from 'react-bootstrap-typeahead';
import {Accordion} from 'react-bootstrap';
import DatePicker from './DatePicker';
import 'react-bootstrap-typeahead/css/Typeahead.css';

// ----- import from local modules -----
import enumList from 'web/utils/enumList';
import {BaseComponent} from 'web/components/BaseComponent';
import {editDispatch} from 'web/redux/actions/DispatchActions';
import {useFetchCalendar} from 'web/modules/dispatch-calendar/hooks/useFetchCalendar';

// ----- constants -----

import {showMessage} from 'web/utils/modalHelper';
import moment from 'moment';

const CalendarModal = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const {
    onAddDispatch,
    mode,
    employeeCode,
    startTime,
    endTime,
    employeeSelection,
    editData,
    dispatchSelection,
    setCalendarModalMode,
  } = props;

  const {
    getCustomerSelection,
    handleAddCase,
    getConditionSelection,
    handleGenerateCaseNumber,
    getAssignmentByCustomerCode,
    setCaseNumber,
    customerSelection,
    conditionSelection,
    caseNumber,
  } = useFetchCalendar(dispatch, showMessage);

  useEffect(() => {
    const baseComponent = new BaseComponent(props); // Use BaseComponent instance if needed
    baseComponent.checkPermission(
      enumList.moduleEnums.dispatchManagement,
      enumList.accessModeEnums.read,
    );
    getCustomerSelection();
  }, []);
  const initialErrors = {
    customer: null,
    dispatch: null,
    accountable: null,
    missingStartTimeEmployees: null,
    validationWrongConfirmTimeEmployees: null,
    startTime: null,
    endTime: null,
  };
  const flgEnum = enumList.assignmentFlgStatus;
  const disabledFlgStatus = [
    flgEnum.FINISH.value,
    flgEnum.DRAFT.value,
    flgEnum.OPEN.value,
  ];
  const baseComponent = new BaseComponent(props);
  const [errors, setErrors] = useState(initialErrors);
  // 人員時段重疊待確認的派工 {req, form, title, names}
  const [overlapConfirm, setOverlapConfirm] = useState(null);
  const [customerData, setCustomerData] = useState([]);
  const [dispatchData, setDispatchData] = useState('');
  const [startTimeModal, setStartTimeModal] = useState('');
  const [endTimeModal, setEndTimeModal] = useState('');
  const [employeeModalList, setEmployeeModalList] = useState([]);
  const [assignmentSelection, setAssignmentSelection] = useState([]);
  const [dispatchAssignmentSelection, setDispatchAssignmentSelection] =
    useState([]);
  const [modalAssignments, setModalAssignments] = useState([]);
  const [employeeSelectionModal, setEmployeeSelectionModal] = useState([]);
  const [newEmployeeStartTimes, setNewEmployeeStartTimes] = useState(new Map());
  const [removedEmployeeEndTimes, setRemovedEmployeeEndTimes] = useState(
    new Map(),
  );
  const [confirmEmployeeStartTime, setConfirmEmployeeStartTime] = useState(
    new Map(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const requiresTimeInput =
    newEmployeeStartTimes.size > 0 ||
    removedEmployeeEndTimes.size > 0 ||
    confirmEmployeeStartTime.size > 0;
  const modalIcon = mode === enumList.modalMode.new ? '#ico-plus' : '#ico-edit';
  const modalTitleText =
    mode === enumList.modalMode.new ? '新增派工' : '編輯派工';
  const disabled =
    mode === enumList.modalMode.edit &&
    disabledFlgStatus.includes(editData?.eventData?.flgStatus);
  // 初始資料
  useEffect(() => {
    if (mode !== enumList.modalMode.edit) return;

    const initEditData = async () => {
      // 統一資料來源
      const data = editData.eventData;
      if (!data?.employees) return;

      const {
        assignmentId,
        workStartTime,
        workEndTime,
        prevEmployee,
        nextEmployee,
      } = data;
      const {_customerCode: customerCode, _customerName: customerName} =
        data.cases || {};
      const isEventDataMode = Boolean(editData.eventData);

      try {
        // 1. 客戶初始化
        getCustomerSelection();
        setCustomerData(customerCode ? [{customerCode, customerName}] : []);

        // 2. Assignment 資料處理
        let finalData = data;
        if (isEventDataMode && customerCode) {
          const assignmentList =
            await getAssignmentByCustomerCode(customerCode);
          const existingAssignment = assignmentList.find(
            (item) => item.assignmentId === assignmentId,
          );

          if (!existingAssignment) assignmentList.push(data);

          setAssignmentSelection(assignmentList);
          setDispatchAssignmentSelection(assignmentList);
          finalData = existingAssignment || data;
        }

        setDispatchData(finalData);

        // 3. 表單設定
        setStartTimeModal(moment(workStartTime).format('YYYY-MM-DDTHH:mm'));
        setEndTimeModal(moment(workEndTime).format('YYYY-MM-DDTHH:mm'));

        // 4. 員工清單
        const employees = finalData.employees.map((emp) => ({
          employeeCode:
            emp.employeeCode === prevEmployee?.employeeCode
              ? nextEmployee?.code
              : emp.employeeCode,
          employeeName:
            emp.employeeCode === prevEmployee?.employeeCode
              ? nextEmployee?.title
              : emp.employeeName,
          isAccountable: Boolean(emp.isAccountable),
        }));

        setEmployeeModalList(employees);
      } catch (error) {
        console.error('初始化失敗:', error);
      }
    };

    initEditData();
    return () => resetForm();
  }, [mode, editData]);

  // 當選擇的派工事項改變時，更新狀態
  useEffect(() => {
    if (Array.isArray(dispatchSelection)) {
      setDispatchAssignmentSelection(dispatchSelection);
    } else {
      setDispatchAssignmentSelection([]);
    }
  }, [dispatchSelection]);

  // 當選擇的客戶改變時，更新狀態 * 日曆自帶新增時間則不重新渲染
  useEffect(() => {
    if (mode !== enumList.modalMode.edit) {
      setStartTimeModal(moment(startTime).format('YYYY-MM-DD HH:mm:ss'));
      setEndTimeModal(moment(endTime).format('YYYY-MM-DD HH:mm:ss'));
      setEmployeeModalList([]);
    }
  }, [startTime, endTime, employeeCode, mode]);
  // 當選擇的員工改變時，更新狀態
  useEffect(() => {
    setEmployeeSelectionModal(
      employeeSelection
        .map((e) => ({
          employeeCode: e.employeeCode,
          employeeName: e.employeeName,
        }))
        .sort((a, b) => a.employeeName.localeCompare(b.employeeName, 'zh')),
    );
  }, [employeeSelection]);

  const validateEmployeeTime = (time, startTimeModal, endTimeModal) => {
    let reason = '';

    if (time && moment(endTimeModal).isSameOrBefore(moment(time))) {
      reason = '人員開始時間不可超過或等於派工結束時間';
    }
    if (time && moment(startTimeModal).isAfter(moment(time))) {
      reason = '人員開始時間不能於派工開始時間之前';
    }

    return reason;
  };

  const checkValid = (
    customerData,
    dispatchData,
    startTimeModal,
    endTimeModal,
    employeeModalList,
    nextNewEmployeeStartTimes,
    nextRemovedEmployeeEndTimes,
    nextConfirmEmployeeStartTime,
  ) => {
    const errors = {...initialErrors};
    const missingStartTimeMap = {};
    const validationWrongConfirmTimeMap = {};
    if (customerData.length === 0) {
      errors.customer = '客戶資料不可為空';
    }
    if (!dispatchData) {
      errors.dispatch = '派工資料不可為空';
    }
    if (startTimeModal === '' || startTimeModal === 'Invalid date') {
      errors.startTime = '開始時間有誤';
    }
    if (endTimeModal === '' || endTimeModal === 'Invalid date') {
      errors.endTime = '結束時間有誤';
    }
    if (!employeeModalList.some((e) => e.isAccountable)) {
      errors.accountable = '未選擇當責員工';
    }
    if (nextNewEmployeeStartTimes.size > 0) {
      nextNewEmployeeStartTimes.forEach(({time}, employeeCode) => {
        const reason = validateEmployeeTime(time, startTimeModal, endTimeModal);
        if (reason) {
          missingStartTimeMap[employeeCode] = true;
          setNewEmployeeStartTimes((prev) => {
            const next = new Map(prev);
            next.set(employeeCode, {
              time,
              reason,
            });
            return next;
          });
        }
        if (!time) {
          missingStartTimeMap[employeeCode] = true;
        }
      });

      if (Object.keys(missingStartTimeMap).length > 0) {
        errors.missingStartTimeEmployees = missingStartTimeMap;
      }
    }

    if (nextConfirmEmployeeStartTime.size) {
      nextConfirmEmployeeStartTime.forEach(
        ({time, isConfirm}, employeeCode) => {
          if (!isConfirm) {
            return (validationWrongConfirmTimeMap[employeeCode] = true);
          }
          const reason = validateEmployeeTime(
            time,
            startTimeModal,
            endTimeModal,
          );
          if (reason) {
            setConfirmEmployeeStartTime((prev) => {
              const next = new Map(prev);
              next.set(employeeCode, {
                time,
                isConfirm,
                reason,
              });
              return next;
            });
            validationWrongConfirmTimeMap[employeeCode] = true;
          }
        },
      );
      if (Object.keys(validationWrongConfirmTimeMap).length > 0) {
        errors.validationWrongConfirmTimeEmployees =
          validationWrongConfirmTimeMap;
      }
    }
    if (Object.values(errors).some((v) => v)) {
      setErrors(errors);
      return false;
    }
    if (
      nextRemovedEmployeeEndTimes.size > 0 &&
      !nextRemovedEmployeeEndTimes
        .keys()
        .every((k) => removedEmployeeEndTimes.has(k))
    ) {
      return false;
    }

    return true;
  };

  // 計算結束時間，以30分鐘為單位
  const calculateEndTime = () => {
    const t = moment();
    const m = t.minute();

    if (m === 0) return t.format('YYYY-MM-DD HH:mm');
    if (m <= 30) return t.minute(30).format('YYYY-MM-DD HH:mm');
    return t.add(1, 'h').minute(0).format('YYYY-MM-DD HH:mm');
  };

  // 判斷員工是否需要補上開始、結束時間
  const getEmployeesRequiringStartAndEndTime = () => {
    let confirmedStartTime = [];
    const timeInfo = {
      reason: '新增人員需補上開始時間',
    };

    if (mode !== enumList.modalMode.edit) {
      return {
        startTimeMap: new Map(),
        endTimeMap: new Map(),
        confirmTimeMap: new Map(),
      };
    }
    const originallyAssignedEmployeeCodes = [
      ...new Set(
        dispatchAssignmentSelection
          .find(({assignmentId}) => assignmentId === dispatchData.assignmentId)
          .employees.map(({employeeCode}) => employeeCode),
      ),
    ];

    const currentlySelectedEmployeeCodes = employeeModalList.map(
      ({employeeCode}) => employeeCode,
    );

    const employeesRequiringEndTime = originallyAssignedEmployeeCodes
      .filter((code) => !currentlySelectedEmployeeCodes.includes(code))
      .map((code) => [code, {time: calculateEndTime()}]);

    const employeesRequiringStartTime = currentlySelectedEmployeeCodes
      .filter((code) => !originallyAssignedEmployeeCodes.includes(code))
      .map((code) => [code, timeInfo]);

    if (!moment(startTimeModal).isSame(moment(dispatchData.workStartTime))) {
      confirmedStartTime = originallyAssignedEmployeeCodes
        .filter(
          (code) =>
            !employeesRequiringEndTime.some(([empCode, _]) => empCode === code),
        )
        .map((code) => {
          if (confirmEmployeeStartTime.has(code)) {
            return [
              code,
              {...confirmEmployeeStartTime.get(code), isConfirm: true},
            ];
          } else {
            const time = dispatchData.employees.find(
              ({employeeCode}) => employeeCode === code,
            )?.workStartTime;

            return [
              code,
              {
                time,
                reason: '派工時間修改需重新確認人員開始時間',
                isConfirm: false,
              },
            ];
          }
        });
    }

    return {
      startTimeMap: new Map(employeesRequiringStartTime),
      endTimeMap: new Map(employeesRequiringEndTime),
      confirmTimeMap: new Map(confirmedStartTime),
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    const form = event.currentTarget;

    // endTimeMap 集合所有需補上結束時間的員工
    const {startTimeMap, endTimeMap, confirmTimeMap} =
      getEmployeesRequiringStartAndEndTime();
    const nextRemovedEmployeeEndTimes = handleEmployeeTimeChange(
      removedEmployeeEndTimes,
      setRemovedEmployeeEndTimes,
      endTimeMap,
    );
    const nextNewEmployeeStartTimes = handleEmployeeTimeChange(
      newEmployeeStartTimes,
      setNewEmployeeStartTimes,
      startTimeMap,
    );
    const nextConfirmEmployeeStartTime = handleEmployeeTimeChange(
      confirmEmployeeStartTime,
      setConfirmEmployeeStartTime,
      confirmTimeMap,
    );

    if (
      !checkValid(
        customerData,
        dispatchData,
        startTimeModal,
        endTimeModal,
        employeeModalList,
        nextNewEmployeeStartTimes,
        nextRemovedEmployeeEndTimes,
        nextConfirmEmployeeStartTime,
      )
    ) {
      setIsLoading(false);
      return;
    }

    form.classList.add('was-validated');

    const req = {
      customerCode: customerData[0].customerCode,
      assignmentId: dispatchData.assignmentId,
      workStartTime: moment(startTimeModal).format('YYYY-MM-DDTHH:mm'),
      workEndTime: moment(endTimeModal).format('YYYY-MM-DDTHH:mm'),
      employeeList: prepareEmployeeData(),
    };
    await submitDispatch(req, form);
  };

  const submitDispatch = async (req, form) => {
    setIsLoading(true);
    setOverlapConfirm(null);
    try {
      const res = await dispatch(editDispatch(req)).unwrap();
      const statusCode = res.code;
      if (statusCode.isSuccessCode()) {
        getCustomerSelection();
        onAddDispatch(req);
        resetForm();
      }
      form.classList.remove('was-validated');
    } catch (error) {
      console.log(error);

      if (parseInt(error.code) === 88893) {
        // 人員時段重疊：改為提醒，確認後仍可派工
        const [title, names] = error.message.split('\n');
        setOverlapConfirm({req, form, title, names});
      } else {
        showMessage(enumList.messageModalMode.error, error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmOverlap = () => {
    const {req, form} = overlapConfirm;
    submitDispatch({...req, allowOverlap: true}, form);
  };

  const prepareEmployeeData = () => {
    const employeeList = employeeModalList.map(
      ({employeeCode, isAccountable}) => ({
        employeeCode,
        isAccountable,
        startTime:
          newEmployeeStartTimes.get(employeeCode)?.time ||
          confirmEmployeeStartTime.get(employeeCode)?.time,
      }),
    );
    if (mode === enumList.modalMode.edit) {
      removedEmployeeEndTimes.forEach(({time: endTime}, code) => {
        employeeList.push({
          employeeCode: code,
          isAccountable: false,
          endTime,
        });
      });
    }
    return employeeList;
  };

  const handleEmployeeTimeChange = (state, setState, newtimeMap) => {
    // 將需要補上結束時間的員工加入狀態內，重複的使用前一次的資料
    let result = new Map();
    if (newtimeMap.size !== 0) {
      result = new Map(state);
      result.keys().forEach((code) => {
        if (!newtimeMap.has(code)) result.delete(code);
      });
      newtimeMap.forEach((timeInfo, code) => {
        if (!result.has(code) || timeInfo.isConfirm) {
          result.set(code, timeInfo);
        }
      });
    }
    setState(result);
    return result;
  };

  // 選好客戸後，取得該客戸尚未派工的清單
  // 這邊的selected是Typeahead的選擇結果，會是一個陣列
  const handleCustomerChange = async (selected) => {
    console.log('selected:', selected);
    setCustomerData(selected);

    if (selected.length > 0) {
      const customerCode = selected[0].customerCode;
      console.log('customerCode:', customerCode);
      const available = 'A';
      const assignmentList = await getAssignmentByCustomerCode(
        customerCode,
        available,
      );
      console.log('取得的無派工清單:', assignmentList);

      setAssignmentSelection(
        assignmentList.filter((item) => !moment(item.workStartTime).isValid()),
      ); // 顯示多選
      setModalAssignments([]); // 清空已選的
    } else {
      setAssignmentSelection([]);
      setModalAssignments([]);
    }
  };

  // 派工事項選擇後，取得該派工事項的assignmentId存檔用
  const handleDispatchChange = (e) => {
    const selectedId = Number(e.target.value);
    const selectedData = assignmentSelection.find(
      (item) => item.assignmentId === selectedId,
    );
    setDispatchData(selectedData);
  };

  const handleResponsibleChange = (e) => {
    const employeeCode = e.target.value;
    const updateObj = {
      employeeCode,
      isAccountable: true,
    };

    const filteredEmployeeModalList = employeeModalList.filter((emp) => {
      return !emp.isAccountable;
    });
    setEmployeeModalList([...filteredEmployeeModalList, updateObj]);
  };

  const handleEmployeeChange = (selected) => {
    const responsible = employeeModalList.filter((emp) => emp.isAccountable);
    if (selected.length === 0) {
      return setEmployeeModalList(responsible);
    }

    const updateList = selected.map((emp) => ({
      ...emp,
      isAccountable: false,
    }));

    setEmployeeModalList([...responsible, ...updateList]);
  };

  const resetForm = () => {
    setCustomerData([]);
    setDispatchData('');
    setStartTimeModal('');
    setEndTimeModal('');
    setEmployeeModalList([]);
    setCustomerData([]);
    setRemovedEmployeeEndTimes(new Map());
    setNewEmployeeStartTimes(new Map());
    setConfirmEmployeeStartTime(new Map());
    setErrors(initialErrors);
    setOverlapConfirm(null);
    setIsLoading(false);
  };

  // 派工內容變更後，先前的重疊確認即失效
  useEffect(() => {
    setOverlapConfirm(null);
  }, [startTimeModal, endTimeModal, employeeModalList]);

  return (
    <div
      ref={ref}
      className="modal fade"
      id="modalCalendar"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabIndex="-1"
      aria-labelledby="modalCalendarLabel"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <form
          className="modal__form modal-content needs-validation"
          id="modalAccountForm"
          noValidate
          style={{
            overflow:
              newEmployeeStartTimes.size === 0 &&
              removedEmployeeEndTimes.size === 0 &&
              confirmEmployeeStartTime.size === 0 &&
              'visible',
          }}
          onSubmit={handleSubmit}
        >
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="modalAccountLabel">
              <div className="modal__title__badge">
                <svg className="badge__icon">
                  <use href={modalIcon}></use>
                </svg>
              </div>
              <div className="modal__title__text">{modalTitleText}</div>
            </h1>
            <button
              type="button"
              className="btn-close btn-cancel"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={resetForm}
            ></button>
          </div>
          <div
            className="modal-body"
            style={{
              overflow:
                newEmployeeStartTimes.size === 0 &&
                removedEmployeeEndTimes.size === 0 &&
                confirmEmployeeStartTime.size === 0 &&
                'visible',
            }}
          >
            <div className="d-flex flex-column gap-3 px-2">
              <div className="modal__form__catagory row">
                <div className="col-12">
                  <label htmlFor="modalDispatchDate" className="form-label">
                    客戶名稱*
                  </label>
                  <Typeahead
                    id="customerName"
                    size="sm"
                    onChange={handleCustomerChange}
                    selected={customerData}
                    inputProps={{required: true}}
                    value={customerData}
                    options={customerSelection}
                    labelKey={(option) =>
                      typeof option === 'string'
                        ? option
                        : option.customerName || ''
                    }
                    renderInput={({
                      inputRef,
                      referenceElementRef,
                      ...inputProps
                    }) => (
                      <>
                        <input
                          {...inputProps}
                          className={`rbt-input-main form-control 
                            rbt-input form-control-sm form-select
                            ${errors.customer && 'is-invalid'}
                            `}
                          ref={(node) => {
                            inputRef(node);
                            referenceElementRef(node);
                          }}
                        />
                        {errors.customer && (
                          <div
                            className="invalid-feedback"
                            style={{display: 'block'}}
                          >
                            {errors.customer}
                          </div>
                        )}
                      </>
                    )}
                    placeholder="請輸入客戶名稱"
                    disabled={mode === enumList.modalMode.edit}
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="modalDispatchDate" className="form-label ">
                    派工事項*
                  </label>
                  <select
                    className={`form-select form-select-sm 
                      ${errors.dispatch && 'is-invalid'}`}
                    id="dispatchName"
                    name="dispatchName"
                    value={String(dispatchData?.assignmentId || '')}
                    onChange={handleDispatchChange}
                    required
                    disabled={
                      mode === enumList.modalMode.edit ||
                      customerData.length === 0
                    }
                  >
                    <option disabled value="">
                      請選擇派工事項
                    </option>
                    {assignmentSelection.map((selection, index) => (
                      <option
                        key={index}
                        value={String(selection.assignmentId)}
                      >
                        {selection.machineSerialNumber} -{' '}
                        {selection.cases._caseNumber}
                      </option>
                    ))}
                  </select>
                  {errors.dispatch && (
                    <div
                      className="invalid-feedback"
                      style={{display: 'block'}}
                    >
                      {errors.dispatch}
                    </div>
                  )}
                </div>
                <div className="col-12">
                  <div className="row">
                    <div className="col-6">
                      <label
                        htmlFor="modalDispatchStartTime"
                        className="form-label"
                      >
                        派工開始時間*
                      </label>
                      <DatePicker
                        id="modalDispatchStartTime"
                        value={startTimeModal}
                        onClose={([date]) => setStartTimeModal(date)}
                        disabled={disabled}
                        error={errors.startTime}
                      />
                      {errors.startTime && (
                        <div
                          className="invalid-feedback"
                          style={{display: 'block'}}
                        >
                          {errors.startTime}
                        </div>
                      )}
                    </div>
                    <div className="col-6">
                      <label
                        htmlFor="modalDispatchEndTime"
                        className="form-label"
                      >
                        派工結束時間*
                      </label>
                      <DatePicker
                        id="modalDispatchEndTime"
                        value={endTimeModal}
                        onClose={([date]) => setEndTimeModal(date)}
                        disabled={disabled}
                        error={errors.endTime}
                      />
                      {errors.endTime && (
                        <div
                          className="invalid-feedback"
                          style={{display: 'block'}}
                        >
                          {errors.endTime}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <label
                        htmlFor="modalDispatchEmployee"
                        className="form-label"
                      >
                        主要負責人員*
                      </label>
                      <select
                        className={`rbt-input-main form-control
                            rbt-input form-control-sm form-select
                            ${errors.accountable && 'is-invalid'}`}
                        id="modalDispatchEmployee"
                        name="modalDispatchEmployee"
                        disabled={disabled}
                        value={
                          employeeModalList.find((e) => e.isAccountable)
                            ?.employeeCode || ''
                        }
                        onChange={handleResponsibleChange}
                        required
                      >
                        <option disabled value="">
                          請選擇指派人員
                        </option>
                        {employeeSelectionModal.map((selection, index) => {
                          const isExist = employeeModalList
                            .filter((emp) => !emp.isAccountable)
                            .some(
                              (emp) =>
                                emp.employeeCode === selection.employeeCode,
                            );
                          return (
                            <option
                              name={selection.employeeName}
                              key={index}
                              disabled={isExist}
                              value={selection.employeeCode}
                            >
                              {isExist
                                ? `${selection.employeeName} 已指派`
                                : selection.employeeName}
                            </option>
                          );
                        })}
                      </select>
                      {errors.accountable && (
                        <div
                          className="invalid-feedback"
                          style={{display: 'block'}}
                        >
                          {errors.accountable}
                        </div>
                      )}
                    </div>
                    <div className="col-6">
                      <label htmlFor="rbt" className="form-label">
                        指派人員
                      </label>
                      <Typeahead
                        id="employeeName"
                        size="sm"
                        onChange={handleEmployeeChange}
                        multiple
                        disabled={disabled}
                        options={employeeSelectionModal.filter(
                          (emp) =>
                            !employeeModalList.some(
                              (e) => e.employeeCode === emp.employeeCode,
                            ),
                        )}
                        selected={employeeModalList.filter(
                          (emp) => !emp.isAccountable,
                        )}
                        labelKey="employeeName"
                        placeholder="請選擇指派人員"
                        renderMenu={(results, menuProps) => (
                          <Menu
                            {...menuProps}
                            maxHeight="150px"
                            className="w-100"
                          >
                            {results.map(
                              ({employeeCode, employeeName}, index) => {
                                const responsibleEmployee =
                                  employeeModalList.find(
                                    (emp) => emp.isAccountable,
                                  )?.employeeCode === employeeCode;

                                return (
                                  <MenuItem
                                    disabled={responsibleEmployee}
                                    option={{employeeCode, employeeName}}
                                    position={index}
                                    key={index}
                                  >
                                    {responsibleEmployee
                                      ? `${employeeName} (主要負責人員)`
                                      : employeeName}
                                  </MenuItem>
                                );
                              },
                            )}
                          </Menu>
                        )}
                      />
                    </div>
                    {requiresTimeInput && (
                      <Accordion className="mt-3">
                        {newEmployeeStartTimes.size > 0 && (
                          <Accordion.Item eventKey="0">
                            <Accordion.Header>新增指派人員</Accordion.Header>
                            <Accordion.Body className="row">
                              {[...newEmployeeStartTimes.entries()].map(
                                ([code, {time, reason}], index) => (
                                  <React.Fragment key={index}>
                                    <div className="col-6">
                                      <label className="form-label">
                                        新指派人員
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        readOnly
                                        value={
                                          employeeSelectionModal.find(
                                            ({employeeCode}) =>
                                              employeeCode === code,
                                          )?.employeeName
                                        }
                                      />
                                    </div>
                                    <div className="col-6">
                                      <label
                                        htmlFor="modalemployeeStartTime"
                                        className="form-label"
                                      >
                                        人員開始時間*
                                      </label>
                                      <DatePicker
                                        id="modalEmployeeStartTime"
                                        value={time}
                                        disabled={disabled}
                                        onClose={([date]) =>
                                          setNewEmployeeStartTimes(
                                            (prev) =>
                                              new Map(
                                                prev.set(code, {
                                                  time: date,
                                                  reason,
                                                }),
                                              ),
                                          )
                                        }
                                        error={
                                          errors?.missingStartTimeEmployees?.[
                                            code
                                          ]
                                        }
                                      />
                                      {errors?.missingStartTimeEmployees?.[
                                        code
                                      ] && (
                                        <div
                                          className="invalid-feedback"
                                          style={{display: 'block'}}
                                        >
                                          {reason}
                                        </div>
                                      )}
                                    </div>
                                  </React.Fragment>
                                ),
                              )}
                            </Accordion.Body>
                          </Accordion.Item>
                        )}
                        {removedEmployeeEndTimes.size > 0 && (
                          <Accordion.Item eventKey="1">
                            <Accordion.Header>結束人員</Accordion.Header>
                            <Accordion.Body className="row">
                              {removedEmployeeEndTimes.size > 0 &&
                                [...removedEmployeeEndTimes.entries()].map(
                                  ([code, {time}], index) => (
                                    <React.Fragment key={index}>
                                      <div className="col-6">
                                        <label className="form-label">
                                          結束人員
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          readOnly
                                          value={
                                            employeeSelectionModal.find(
                                              ({employeeCode}) =>
                                                employeeCode === code,
                                            )?.employeeName
                                          }
                                        />
                                      </div>
                                      <div className="col-6">
                                        <label
                                          htmlFor="modalEmployeeEndTime"
                                          className="form-label"
                                        >
                                          人員結束時間*
                                        </label>
                                        <DatePicker
                                          id="modalEmployeeEndTime"
                                          value={time}
                                          disabled={disabled}
                                          onClose={([date]) =>
                                            setRemovedEmployeeEndTimes(
                                              (prev) =>
                                                new Map(
                                                  prev.set(code, {
                                                    time: date,
                                                  }),
                                                ),
                                            )
                                          }
                                        />
                                      </div>
                                    </React.Fragment>
                                  ),
                                )}
                            </Accordion.Body>
                          </Accordion.Item>
                        )}
                        {confirmEmployeeStartTime.size > 0 && (
                          <Accordion.Item eventKey="2">
                            <Accordion.Header>
                              重新確認指派開始時間
                            </Accordion.Header>
                            <Accordion.Body className="row">
                              {[...confirmEmployeeStartTime.entries()].map(
                                ([code, {time, reason, isConfirm}], index) => (
                                  <React.Fragment key={index}>
                                    <div className="col-6">
                                      <label className="form-label">
                                        確認指派人員
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        readOnly
                                        value={
                                          employeeSelectionModal.find(
                                            ({employeeCode}) =>
                                              employeeCode === code,
                                          )?.employeeName
                                        }
                                      />
                                    </div>
                                    <div className="col-6">
                                      <label
                                        htmlFor="modalemployeeConfirmStartTime"
                                        className="form-label"
                                      >
                                        人員開始時間
                                      </label>
                                      <DatePicker
                                        id="modalEmployeeConfirmStartTime"
                                        value={time}
                                        disabled={disabled}
                                        onClose={([date]) =>
                                          setConfirmEmployeeStartTime(
                                            (prev) =>
                                              new Map(
                                                prev.set(code, {
                                                  time: date,
                                                  reason,
                                                  isConfirm,
                                                }),
                                              ),
                                          )
                                        }
                                        error={
                                          errors
                                            ?.validationWrongConfirmTimeEmployees?.[
                                            code
                                          ]
                                        }
                                      />
                                      {errors
                                        .validationWrongConfirmTimeEmployees?.[
                                        code
                                      ] && (
                                        <div
                                          className="invalid-feedback"
                                          style={{display: 'block'}}
                                        >
                                          {reason}
                                        </div>
                                      )}
                                    </div>
                                  </React.Fragment>
                                ),
                              )}
                            </Accordion.Body>
                          </Accordion.Item>
                        )}
                      </Accordion>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!disabled && overlapConfirm && (
            // 提示放在 footer 上方；footer 為 nowrap 且按鈕寬度 100%，放在內部會被擠壓
            <div className="px-3 pt-3 pb-3">
              <div className="alert alert-warning mb-0 py-2" role="alert">
                <div>{overlapConfirm.title}：{overlapConfirm.names}</div>
                <small>確定仍要派工嗎？</small>
              </div>
            </div>
          )}
          <div className="modal-footer">
            {!disabled && overlapConfirm && (
              <>
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => setOverlapConfirm(null)}
                >
                  返回修改
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={isLoading}
                  onClick={handleConfirmOverlap}
                >
                  仍要派工
                </button>
              </>
            )}
            {!disabled && !overlapConfirm && (
              <>
                <button
                  type="button"
                  className="btn btn-cancel"
                  data-bs-dismiss="modal"
                  onClick={() => resetForm()}
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  儲存
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
});

export default CalendarModal;
